import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonical?: string;
  schema?: object;
}

export const useSEO = ({ title, description, keywords, canonical, schema }: SEOProps) => {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://prtproprete.com';

  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);

    // Canonical URL
    const canonicalUrl = canonical || `${siteUrl}${location.pathname}`;
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', canonicalUrl);

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', canonicalUrl, true);
    updateMetaTag('og:type', 'website', true);

    // Twitter Card tags
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);

    // Schema.org JSON-LD
    if (schema) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]');
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(schema);
    }

    // Last modified
    updateMetaTag('last-modified', new Date().toISOString());
  }, [title, description, keywords, canonical, schema, location.pathname, siteUrl]);
};

// Schema.org generators
export const generateLocalBusinessSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://prtproprete.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${siteUrl}/#business`,
    "name": "PRT Propreté",
    "image": "https://static.readdy.ai/image/a17d3ae5e640a18a289681692364ae47/58da3f7e4785850c57041e6c71714363.png",
    "description": "PRT Proprete - Préparateur auto professionnel à Creil (60). Service de lavage automobile premium avec Pack Premium 60€ et Pack Luxe 80€. Déplacement gratuit dans un rayon de 35kms.",
    "url": siteUrl,
    "telephone": "+33776969414",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Creil",
      "addressRegion": "Oise",
      "postalCode": "60",
      "addressCountry": "FR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "49.2597",
      "longitude": "2.4750"
    },
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "49.2597",
        "longitude": "2.4750"
      },
      "geoRadius": "35000"
    },
    "priceRange": "60€-80€",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday"
        ],
        "opens": "10:00",
        "closes": "17:00"
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Services de lavage automobile",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Pack Premium",
            "description": "Nettoyage intérieur complet"
          },
          "price": "60",
          "priceCurrency": "EUR"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Pack Luxe",
            "description": "Nettoyage intérieur et extérieur complet"
          },
          "price": "80",
          "priceCurrency": "EUR"
        }
      ]
    }
  };
};

export const generateWebPageSchema = (pageName: string, description: string) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://prtproprete.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": pageName,
    "description": description,
    "url": siteUrl,
    "inLanguage": "fr-FR",
    "isPartOf": {
      "@type": "WebSite",
      "name": "PRT Propreté",
      "url": siteUrl
    }
  };
};

export const generateServiceSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://prtproprete.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Lavage automobile professionnel",
    "provider": {
      "@type": "LocalBusiness",
      "name": "PRT Propreté",
      "url": siteUrl
    },
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "49.2597",
        "longitude": "2.4750"
      },
      "geoRadius": "35000"
    },
    "offers": [
      {
        "@type": "Offer",
        "name": "Pack Premium",
        "description": "Nettoyage intérieur complet incluant moquette, tapis, plastiques, shampouinage des sièges, vitres et entourage des portes",
        "price": "60",
        "priceCurrency": "EUR"
      },
      {
        "@type": "Offer",
        "name": "Pack Luxe",
        "description": "Nettoyage intérieur et extérieur complet incluant tous les services du Pack Premium plus le nettoyage extérieur et des jantes",
        "price": "80",
        "priceCurrency": "EUR"
      }
    ]
  };
};

export const generateImageGallerySchema = (images: Array<{url: string; title: string; description: string}>) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://prtproprete.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    "name": "Galerie Avant/Après - PRT Propreté",
    "description": "Découvrez la qualité de nos prestations premium à travers nos images avant/après",
    "url": `${siteUrl}/galerie`,
    "image": images.map(img => ({
      "@type": "ImageObject",
      "url": img.url,
      "name": img.title,
      "description": img.description
    }))
  };
};

export const generateContactPageSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://prtproprete.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact - PRT Propreté",
    "description": "Contactez-nous pour réserver votre prestation de lavage automobile premium",
    "url": `${siteUrl}/contact`,
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": "PRT Propreté",
      "telephone": "+33776969414",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Creil",
        "addressRegion": "Oise",
        "postalCode": "60",
        "addressCountry": "FR"
      }
    }
  };
};
